'use client';

import { c as _c } from "react/compiler-runtime";
import { useGetPublicPartnerCreator } from '@morfi/backend-sdk-v2/client';
import { PortfolioPnlCard } from '@/components/portfolio/cards/portfolio-pnl-card';
import { ProfilePortfolioCard } from '@/components/portfolio/cards/profile-portfolio-card';
import { CreatedPanel } from '@/components/portfolio/created/created-panel';
import { FaucetBanner } from '@/components/portfolio/faucet-banner';
import { useResolvedSelection } from '@/components/portfolio/header/use-resolved-selection';
import { usePortfolioContext } from '@/components/portfolio/portfolio-context';
import { PortfolioPositionsCard } from '@/components/portfolio/positions-card/portfolio-positions-card';
import { ReferralsPanel } from '@/components/portfolio/referrals/referrals-panel';
import { GiftsSummaryCard } from '@/components/portfolio/summary-cards/gifts-summary-card';
import { PortfolioActivityPanel } from '@/components/portfolio/tabs/portfolio-activity-panel';
import { UnauthenticatedPrompt } from '@/components/portfolio/unauthenticated-prompt';
import { StreakWidget } from '@/components/streaks/streak-widget';
import { UserPortfolioSnapshotQueryOwner } from '@/hooks/positions/user-portfolio/query-owner';
import { useFeatureGate } from '@/hooks/use-feature-gate';
import { checkGate } from '@/lib/analytics';
import type { Wallet } from '@/types/auth';
import { PartnerLoadError } from './partner/partner-load-error';
import { PartnerVideo } from './partner/partner-video';
interface ConnectedProvider {
  provider: string;
  username?: string;
  displayName?: string;
}
export interface PortfolioBodyProps {
  creatorUserId: string | null;
  registeredAt: string | null;
  /** Active wallets — own variant passes its wallet list, other variant passes []. */
  wallets: Wallet[];
  /**
   * Primary wallet for the viewer. Own viewer passes the auth user's primary
   * wallet. Other viewer has no wallet info — pass null.
   */
  primaryWallet: Wallet | null;
  /** Identity passthrough — header reads these instead of `useAuth()`. */
  displayName: string | null;
  handle: string | null;
  bio: string | null;
  avatarUrl: string | null;
  /** Spec 071 NSFW flag for the avatar URL. */
  avatarNsfw?: boolean | null;
  /** `users.avatar_preferences.rasterise` — drives the avatar render mode. */
  avatarRasterise?: boolean | null;
  heroUrl: string | null;
  /** Spec 071 NSFW flag for the cover URL. */
  coverNsfw?: boolean | null;
  connectedProviders: ConnectedProvider[];
}
export function PortfolioBody(t0) {
  const $ = _c(106);
  const {
    creatorUserId,
    registeredAt,
    wallets,
    primaryWallet,
    displayName,
    handle,
    bio,
    avatarUrl,
    avatarNsfw,
    avatarRasterise,
    heroUrl,
    coverNsfw,
    connectedProviders
  } = t0;
  const {
    viewerMode,
    userId,
    financialQueryId,
    primaryWalletPubkey,
    walletPubkey: contextWalletPubkey,
    walletPubkeys: contextWalletPubkeys,
    multiWalletEnabled
  } = usePortfolioContext();
  const isViewerOwn = viewerMode === "own";
  const partnerEnabled = useFeatureGate("creator_and_platform_markets");
  const t1 = creatorUserId ?? "";
  let t2;
  if ($[0] !== t1) {
    t2 = {
      id: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = partnerEnabled === true && creatorUserId !== null;
  let t4;
  if ($[2] !== t3) {
    t4 = {
      enabled: t3,
      retry: false
    };
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const partnerQuery = useGetPublicPartnerCreator(t2, t4);
  const partnerError = partnerQuery.error;
  const missingPartner = partnerError !== null && "httpStatus" in partnerError && partnerError.httpStatus === 404;
  const partnerProfile = partnerEnabled === true ? partnerQuery.data : undefined;
  const {
    mode,
    wallet
  } = useResolvedSelection(wallets);
  const isSingle = multiWalletEnabled ? mode === "single" && wallet !== null : primaryWalletPubkey !== null;
  const activeWallets = wallets.filter(_temp);
  const realtimeWalletPubkey = multiWalletEnabled && isSingle && wallet !== null ? wallet.address : contextWalletPubkey;
  let t5;
  if ($[4] !== contextWalletPubkeys || $[5] !== isSingle || $[6] !== multiWalletEnabled || $[7] !== wallet) {
    t5 = multiWalletEnabled && isSingle && wallet !== null ? [wallet.address] : contextWalletPubkeys;
    $[4] = contextWalletPubkeys;
    $[5] = isSingle;
    $[6] = multiWalletEnabled;
    $[7] = wallet;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const realtimeWalletPubkeys = t5;
  let t6;
  if ($[9] !== isViewerOwn || $[10] !== userId) {
    t6 = isViewerOwn ? [userId] : [];
    $[9] = isViewerOwn;
    $[10] = userId;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const realtimeGiftUserIds = t6;
  if (isViewerOwn && activeWallets.length === 0) {
    let t7;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <UnauthenticatedPrompt />;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    return t7;
  }
  let t7;
  if ($[13] !== isViewerOwn) {
    t7 = isViewerOwn && checkGate("gifting");
    $[13] = isViewerOwn;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const giftsOn = t7;
  let t8;
  if ($[15] !== isViewerOwn) {
    t8 = isViewerOwn && checkGate("referrals");
    $[15] = isViewerOwn;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const referralsOn = t8;
  let t9;
  if ($[17] !== missingPartner || $[18] !== partnerEnabled || $[19] !== partnerQuery) {
    t9 = partnerEnabled && partnerQuery.isError && !missingPartner ? <PartnerLoadError onRetry={() => void partnerQuery.refetch()} /> : null;
    $[17] = missingPartner;
    $[18] = partnerEnabled;
    $[19] = partnerQuery;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== contextWalletPubkeys || $[22] !== isViewerOwn || $[23] !== userId) {
    t10 = !isViewerOwn ? <UserPortfolioSnapshotQueryOwner userId={userId} knownWallets={contextWalletPubkeys} /> : null;
    $[21] = contextWalletPubkeys;
    $[22] = isViewerOwn;
    $[23] = userId;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const t11 = partnerProfile ? "flex flex-col gap-4" : "grid grid-cols-1 gap-4 md:grid-cols-5";
  const t12 = partnerProfile ? "flex min-w-0 flex-col gap-4 md:flex-row md:items-stretch" : "min-w-0 md:col-span-2";
  let t13;
  if ($[25] !== partnerProfile) {
    t13 = partnerProfile ? {
      partnerProfile
    } : {};
    $[25] = partnerProfile;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  const t14 = partnerProfile ? "min-w-0 flex-1" : "md:h-full";
  const t15 = partnerProfile?.displayName ?? displayName;
  const t16 = partnerProfile?.avatarUrl ?? avatarUrl;
  const t17 = avatarNsfw ?? null;
  const t18 = avatarRasterise ?? null;
  const t19 = partnerProfile ? partnerProfile.bannerUrl : heroUrl;
  const t20 = coverNsfw ?? null;
  let t21;
  if ($[27] !== bio || $[28] !== connectedProviders || $[29] !== creatorUserId || $[30] !== handle || $[31] !== realtimeWalletPubkeys || $[32] !== t13 || $[33] !== t14 || $[34] !== t15 || $[35] !== t16 || $[36] !== t17 || $[37] !== t18 || $[38] !== t19 || $[39] !== t20) {
    t21 = <ProfilePortfolioCard creatorUserId={creatorUserId} {...t13} walletPubkeys={realtimeWalletPubkeys} className={t14} displayName={t15} handle={handle} bio={bio} avatarUrl={t16} avatarNsfw={t17} avatarRasterise={t18} heroUrl={t19} coverNsfw={t20} connectedProviders={connectedProviders} />;
    $[27] = bio;
    $[28] = connectedProviders;
    $[29] = creatorUserId;
    $[30] = handle;
    $[31] = realtimeWalletPubkeys;
    $[32] = t13;
    $[33] = t14;
    $[34] = t15;
    $[35] = t16;
    $[36] = t17;
    $[37] = t18;
    $[38] = t19;
    $[39] = t20;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== partnerProfile) {
    t22 = partnerProfile?.videoUrl ? <PartnerVideo key={partnerProfile.videoUrl} url={partnerProfile.videoUrl} /> : null;
    $[41] = partnerProfile;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] !== t12 || $[44] !== t21 || $[45] !== t22) {
    t23 = <div className={t12}>{t21}{t22}</div>;
    $[43] = t12;
    $[44] = t21;
    $[45] = t22;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== financialQueryId || $[48] !== realtimeWalletPubkey || $[49] !== realtimeWalletPubkeys || $[50] !== registeredAt || $[51] !== userId || $[52] !== viewerMode || $[53] !== wallets) {
    t24 = <div className="min-w-0 md:col-span-3 md:h-full"><PortfolioPnlCard wallets={wallets} registeredAt={registeredAt} viewerMode={viewerMode} userId={financialQueryId} snapshotUserId={userId} walletPubkey={realtimeWalletPubkey} walletPubkeys={realtimeWalletPubkeys} /></div>;
    $[47] = financialQueryId;
    $[48] = realtimeWalletPubkey;
    $[49] = realtimeWalletPubkeys;
    $[50] = registeredAt;
    $[51] = userId;
    $[52] = viewerMode;
    $[53] = wallets;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  if ($[55] !== t11 || $[56] !== t23 || $[57] !== t24) {
    t25 = <div className={t11}>{t23}{t24}</div>;
    $[55] = t11;
    $[56] = t23;
    $[57] = t24;
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  let t26;
  if ($[59] !== isViewerOwn || $[60] !== primaryWallet || $[61] !== wallets) {
    t26 = isViewerOwn ? <FaucetBanner wallets={wallets} primaryWallet={primaryWallet} /> : null;
    $[59] = isViewerOwn;
    $[60] = primaryWallet;
    $[61] = wallets;
    $[62] = t26;
  } else {
    t26 = $[62];
  }
  let t27;
  if ($[63] !== realtimeWalletPubkeys) {
    t27 = realtimeWalletPubkeys.join(",");
    $[63] = realtimeWalletPubkeys;
    $[64] = t27;
  } else {
    t27 = $[64];
  }
  const t28 = `${userId}:${t27}`;
  const t29 = isSingle ? realtimeWalletPubkey : financialQueryId;
  let t30;
  if ($[65] !== realtimeWalletPubkey || $[66] !== realtimeWalletPubkeys || $[67] !== t28 || $[68] !== t29 || $[69] !== userId) {
    t30 = <PortfolioPositionsCard key={t28} userId={realtimeWalletPubkey} financialQueryId={t29} snapshotUserId={userId} walletPubkeys={realtimeWalletPubkeys} />;
    $[65] = realtimeWalletPubkey;
    $[66] = realtimeWalletPubkeys;
    $[67] = t28;
    $[68] = t29;
    $[69] = userId;
    $[70] = t30;
  } else {
    t30 = $[70];
  }
  let t31;
  if ($[71] !== realtimeWalletPubkeys) {
    t31 = realtimeWalletPubkeys.join(",");
    $[71] = realtimeWalletPubkeys;
    $[72] = t31;
  } else {
    t31 = $[72];
  }
  const t32 = `activity:${userId}:${t31}`;
  const t33 = multiWalletEnabled ? null : primaryWalletPubkey;
  let t34;
  if ($[73] !== isViewerOwn || $[74] !== realtimeGiftUserIds || $[75] !== realtimeWalletPubkeys || $[76] !== t32 || $[77] !== t33 || $[78] !== userId) {
    t34 = <PortfolioActivityPanel key={t32} userId={userId} walletPubkeys={realtimeWalletPubkeys} giftUserIds={realtimeGiftUserIds} isOwn={isViewerOwn} attributionWalletAddress={t33} />;
    $[73] = isViewerOwn;
    $[74] = realtimeGiftUserIds;
    $[75] = realtimeWalletPubkeys;
    $[76] = t32;
    $[77] = t33;
    $[78] = userId;
    $[79] = t34;
  } else {
    t34 = $[79];
  }
  let t35;
  if ($[80] !== creatorUserId || $[81] !== displayName || $[82] !== handle || $[83] !== partnerProfile) {
    t35 = partnerProfile !== undefined && creatorUserId !== null ? <CreatedPanel userId={creatorUserId} handle={handle} displayName={displayName} /> : null;
    $[80] = creatorUserId;
    $[81] = displayName;
    $[82] = handle;
    $[83] = partnerProfile;
    $[84] = t35;
  } else {
    t35 = $[84];
  }
  let t36;
  if ($[85] !== creatorUserId || $[86] !== displayName || $[87] !== handle || $[88] !== partnerProfile) {
    t36 = partnerProfile === undefined && creatorUserId !== null ? <CreatedPanel userId={creatorUserId} handle={handle} displayName={displayName} /> : null;
    $[85] = creatorUserId;
    $[86] = displayName;
    $[87] = handle;
    $[88] = partnerProfile;
    $[89] = t36;
  } else {
    t36 = $[89];
  }
  let t37;
  if ($[90] !== giftsOn || $[91] !== isViewerOwn || $[92] !== realtimeWalletPubkeys || $[93] !== referralsOn || $[94] !== userId) {
    t37 = isViewerOwn ? <EngagementCards userId={userId} walletPubkeys={realtimeWalletPubkeys} giftsOn={giftsOn} referralsOn={referralsOn} /> : null;
    $[90] = giftsOn;
    $[91] = isViewerOwn;
    $[92] = realtimeWalletPubkeys;
    $[93] = referralsOn;
    $[94] = userId;
    $[95] = t37;
  } else {
    t37 = $[95];
  }
  let t38;
  if ($[96] !== t10 || $[97] !== t25 || $[98] !== t26 || $[99] !== t30 || $[100] !== t34 || $[101] !== t35 || $[102] !== t36 || $[103] !== t37 || $[104] !== t9) {
    t38 = <main className="w-full space-y-4">{t9}{t10}{t25}{t26}{t30}{t34}{t35}{t36}{t37}</main>;
    $[96] = t10;
    $[97] = t25;
    $[98] = t26;
    $[99] = t30;
    $[100] = t34;
    $[101] = t35;
    $[102] = t36;
    $[103] = t37;
    $[104] = t9;
    $[105] = t38;
  } else {
    t38 = $[105];
  }
  return t38;
}
function _temp(w) {
  return w.archivedAt === null;
}
function EngagementCards(t0) {
  const $ = _c(12);
  const {
    userId,
    walletPubkeys,
    giftsOn,
    referralsOn
  } = t0;
  let t1;
  if ($[0] !== userId || $[1] !== walletPubkeys) {
    t1 = <StreakWidget userId={userId} walletPubkeys={walletPubkeys} />;
    $[0] = userId;
    $[1] = walletPubkeys;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== giftsOn) {
    t2 = giftsOn ? <GiftsSummaryCard /> : null;
    $[3] = giftsOn;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== referralsOn || $[6] !== userId) {
    t3 = referralsOn ? <ReferralsPanel userId={userId} /> : null;
    $[5] = referralsOn;
    $[6] = userId;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t4 = <div data-testid="portfolio-engagement-row" className="grid grid-cols-1 gap-4 md:grid-cols-2">{t1}{t2}{t3}</div>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}